/**
 * Soporte 9 F1 — WHICH engines the "Ignore object owner" compare option
 * (`options.ignoreOwner`) applies to.
 *
 * Same shape and same reasoning as `wrapCapableEngines.ts` next door: the
 * answer has to be given by code in two processes that can never import each
 * other — the renderer (the Setup checkbox in `SchemaCompareInlineOptions`,
 * the options-file import in `compareOptionsFile.ts`) and the main process
 * (the comparer's owner-fold gate in `schemaComparer.ts`, the MCP
 * `owner-ignored` disclosure in `mcp-compare/schemaCompareDisclosure.ts`).
 * The review that created this module found the policy written once, as the
 * checkbox's literal `engine === 'mssql'`, while the comparer, the results
 * banner and the options-file import were engine-agnostic — so one banner
 * click (or one loaded options file) on a non-MSSQL comparison could activate
 * an option no UI on that engine could display or clear again.
 *
 * ## Why MSSQL, and only MSSQL
 *
 * The option exists for engines whose catalog stores module text VERBATIM as
 * typed, so that `CREATE PROCEDURE [dbo].[x]` on one side and
 * `CREATE PROCEDURE [x]` on the other are different stored strings for the
 * same catalog object. That is `sys.sql_modules`. The other engines either
 * canonicalise definitions at read time (PostgreSQL's `pg_get_functiondef`/
 * `pg_get_viewdef`), never store a schema qualifier in the header (MySQL),
 * or already have their provider-authored owner neutralised by #120 §2.5
 * (Oracle, MySQL views). Widening this list means verifying the new engine
 * actually stores divergent header spellings for identical objects — and
 * teaching its Setup dialog to render the checkbox, or the invisible-option
 * defect returns for that engine.
 */
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

export const OWNER_IGNORE_CAPABLE_ENGINES: readonly DatabaseEngineId[] = ['mssql'];

/**
 * Accepts plain `string` (not just `DatabaseEngineId`) because the MCP
 * operation record's engine union carries `'mock'` — an unknown or absent
 * engine answers `false`, which is the safe direction everywhere this gates.
 */
export const engineSupportsIgnoreOwner = (engine: string | undefined): boolean =>
  engine !== undefined && (OWNER_IGNORE_CAPABLE_ENGINES as readonly string[]).includes(engine);
